import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from "../../helpers/useStores";
import { styled, darkTheme } from '../../stitches.config';
import { bodyLarge, h1, h3 } from '../commonStyles/Fonts';
import Breadcrumbs from "../../redesign/components/Breadcrumbs";

import { ReactComponent as PlayIcon } from "../../assets/img/podcastPlayer/podcast_play.svg?react";
import PodcastsList from '../components/PodcastsList';
import { useParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import Media from '../../components/common/Media';
import { usePodcastPlayer } from '../../context/PodcastPlayerContext';
import Link from '../../components/Link';
import useMediaQuery from '../../helpers/useMediaQuery';
import Footer from "../components/Footer"

const PodcastSeriePage: React.FC = observer(() => {

    const { PodcastStore } = useStores();

    const { slug } = useParams<{ slug: string }>();

    const { setEpisode } = usePodcastPlayer();

    useEffect(() => { PodcastStore.updateSlug(slug) }, [slug]);

    const [lightTheme, setLightTheme] = useState<boolean>(false);

    const checkBightness = (image: HTMLImageElement) => {
        if (!image) return;

        image.crossOrigin = "Anonymous";
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.width;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.drawImage(image, 0, 0);
            const imageData = ctx.getImageData(0, 0, image.width, image.height).data;

            let colorSum = 0;
            for (let i = 0; i < imageData.length; i += 4) {
                const r = imageData[i];
                const g = imageData[i + 1];
                const b = imageData[i + 2];
                colorSum += Math.floor((r + g + b) / 3);
            }
            const brightness = Math.floor(colorSum / (image.width * image.height));

            console.log('[ES] bright', brightness);

            setLightTheme(brightness > 48);
        };
    };

    const isMobile = useMediaQuery('mobile');

    const podcastSerie = PodcastStore.podcastSerie;
    const podcastSerieEpisodes = PodcastStore.podcastSerieEpisodes ?? [];

    //TODO: Cleaner way...
    if (PodcastStore.status === "error")
        return <Error error={PodcastStore.message} />;
    if (PodcastStore.status === "loading" || podcastSerie == undefined)
        return <Loader />;

    return (
        <>
            <PodcastSerieHeader>
                <section className={`container ${lightTheme ? '' : darkTheme}`}>
                    <Breadcrumbs
                        category={{
                            name: 'Podcasty',
                            link: '/podcasty'
                        }}
                        invert={lightTheme}
                    />
                    <div className="row">
                        <div className="col-md-8 col-lg-7 header-col">
                            <PodcastPoster>
                                <PodcastPosterWrapper>
                                    <PodcastPosterMediaWrapper>
                                        <Media
                                            media={podcastSerie.poster.media}
                                            src={podcastSerie.poster.img}
                                        />
                                    </PodcastPosterMediaWrapper>
                                    <button><PlayIcon /></button>
                                </PodcastPosterWrapper>
                            </PodcastPoster>
                            <h1 className={h1()}>{podcastSerie.title}</h1>
                            <p className={`${bodyLarge()} author`}>{podcastSerie.author.name}</p>
                            <p className={bodyLarge()}>{podcastSerie.lead}</p>
                            {podcastSerieEpisodes.length > 0 && <Link to={podcastSerieEpisodes[0].url}>
                                <NewestPodcastButton className={bodyLarge()} onClick={() => isMobile && setEpisode({
                                    episode_id: podcastSerieEpisodes[0].OID,
                                    video_id: podcastSerieEpisodes[0].videoId
                                })}>
                                    <PlayIcon /> Najnowszy odcinek
                                </NewestPodcastButton>
                            </Link>}
                        </div>
                        <PodcastSerieAutorRow className="col-md-4 col-lg-3 col-lg-offset-2 author-col">
                            <PodcastSerieAutor>
                                <img src={podcastSerie.author.img} />
                                <p className={h3()}>{podcastSerie.author.name}</p>
                            </PodcastSerieAutor>
                            <p className={bodyLarge()}>
                                {podcastSerie.author.description}
                            </p>
                            {podcastSerie.author.mail && <p className={bodyLarge()}>
                                Napisz do autora:<br />
                                <a href={`mailto:${podcastSerie.author.mail}`}>{podcastSerie.author.mail}</a>
                            </p>}
                        </PodcastSerieAutorRow>
                    </div >
                </section >
            </PodcastSerieHeader >
            {podcastSerieEpisodes &&
                <section className="container">
                    <div className="row">
                        <div className="col-md-9 col-lg-8">
                            <PodcastsList
                                items={podcastSerieEpisodes}
                                title={"Ostatnio dodane odcinki"}
                            />
                        </div>
                        <AdPlaceholder className="col-md-3 hidden-lg-up">
                            #adContent
                        </AdPlaceholder>
                    </div>
                </section>
            }
            <Footer />
        </>

    );

});

export default PodcastSeriePage;

const PodcastSerieHeader = styled('div', {
    position: 'relative',
    background: '$grayscale100Fixed',
    '& > .container': {
        position: 'static'
    },
    '& .author-col': {
        position: 'relative',
        zIndex: '2',
        marginTop: '3rem',
        '&::before': {
            content: '',
            position: 'absolute',
            zIndex: '-1',
            width: '100vw',
            height: '100%',
            top: 0,
            left: 'calc(50% - 50vw)',
            background: '#F5F5F5',
            '@bp4': {
                display: 'none'
            }
        },
        background: '#F5F5F5',
        padding: '3.2rem 1.6rem 4.0rem',
        '@bp4': {
            marginTop: '8rem',
        }
    },
    '& .header-col': {
        position: 'static',
        '& h1, & p': {
            color: '$grayscale0Fixed',
            '@bp4': {
                color: '$grayscale100'
            }
        },
        '& p.author': {
            color: '#8D8D8D',
            '@bp4': {
                color: '$grayscale75'
            }
        },
        '& h1': {
            margin: '2.4rem 0 0.4rem',
            '@bp4': {
                margin: '8rem 0 0.8rem'
            }
        },
    },
    '& .breadcrumbs': {
        marginTop: '3rem'
    },
    '& .row': {
        minHeight: '480px'
    },
    '@bp4': {
        background: 'transparent'
    },
});

const PodcastPoster = styled('div', {
    position: 'relative',
    marginTop: '2rem',
    width: '100%',
    paddingTop: '100%',
    display: 'flex',
    '& button': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        display: 'flex',
        width: '4.8rem',
        height: '4.8rem',
        background: 'transparent',
        border: '3px solid $grayscale100',
        borderRadius: '100%',
        zIndex: 10,
        '& svg': {
            fill: '$grayscale100',
            margin: 'auto',
            transform: 'translateX(2px)',
        },
    },
    '@bp4': {
        margin: 0,
        padding: 0,
        zIndex: '-1',
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: 'calc(100% - 3rem)',
        '& button': {
            display: 'none'
        }
    },
    '@bp5': {
        height: 'calc(100% - 6rem)',
    },
});

const PodcastPosterWrapper = styled('div', {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    'img': {
        objectFit: 'cover',
        objectPosition: 'center'
    }
});

const PodcastPosterMediaWrapper = styled('div', {
    width: '100%',
    position: 'relative',
    '.placeholder-wrapper img': {
        width: "100%",
        height: "350px",
        minHeight: "100%",
        objectFit: "cover"
    }
});

const NewestPodcastButton = styled('button', {
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    background: '$grayscale100',
    padding: '1.25rem 2rem',
    marginTop: '2.4rem',
    color: '$grayscale0',
    justifyContent: 'center',
    '&:hover': {
        color: '$grayscale0',
    },
    'svg': {
        fill: '$grayscale0',
        marginRight: '1.5rem'
    },
    '@bp3': {
        display: 'inline-flex',
        justifyContent: 'flex-start'
    }
});

const PodcastSerieAutorRow = styled('div', {
    color: '#8D8D8D',
    '& p': {
        margin: '2.4rem 0 0',
        '& a': {
            color: '#8D8D8D',
        }
    }
});

const PodcastSerieAutor = styled('div', {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '2.4rem',
    '& p': {
        margin: '0',
        color: '$grayscale100Fixed'
    },
    '& img': {
        width: '7.2rem',
        height: '7.2rem',
        borderRadius: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        marginRight: '1.6rem',
        background: 'rgba(0,0,0,.4)'
    }
});

const AdPlaceholder = styled('div', {
    padding: '4rem 0',
    display: 'none',
    '@bp4': {
        display: 'block',
        padding: 0,
        marginTop: '160px'
    }
});